import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';

import type { TaxonomyStudioContextValue } from '../context/taxonomy-studio-context';
import type { Release, ReleaseRef } from '../types/taxonomy-studio.types';

import { useDraft, useReleases, useTaxonomyPermissions } from './use-taxonomy-draft';

/**
 * Builds the studio context from the URL's `?release=`, the polled draft and the
 * release list. Default ref is `draft` — the API reads live when no draft is open,
 * so "draft if open else live" needs no extra round-trip. Editable only at the head:
 * the draft, or live/its number while no draft exists.
 */
export function useTaxonomyStudioState(): TaxonomyStudioContextValue {
  const search = useSearch({ from: '/_authenticated/_tenant/taxonomy' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: draft = null } = useDraft();
  const { data: releases = [] } = useReleases();
  const { data: permissions } = useTaxonomyPermissions();
  const knownVersion = useTaxonomyStudioStore((s) => s.knownVersion);
  const knownSeq = useTaxonomyStudioStore((s) => s.draftSeq);
  const setKnown = useTaxonomyStudioStore((s) => s.setKnown);

  const releaseRef: ReleaseRef = search.release ?? 'draft';
  const live = releases.find((r) => r.status === 'ACTIVE') ?? null;

  const setReleaseRef = useCallback(
    (ref: ReleaseRef | undefined) => {
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({ ...prev, release: ref }),
      });
    },
    [navigate],
  );

  const acknowledgeOthers = useCallback(() => {
    if (draft?.draftVersion != null) setKnown(draft.seq, draft.draftVersion);
    void qc.invalidateQueries({ queryKey: queryKeys.taxonomyStudio.all });
  }, [draft, qc, setKnown]);

  return useMemo(() => {
    const isHead =
      releaseRef === 'draft' ||
      (draft !== null && releaseRef === String(draft.seq)) ||
      (draft === null &&
        (releaseRef === 'live' || (live !== null && releaseRef === String(live.seq))));
    const isLiveWhileDraft =
      draft !== null &&
      (releaseRef === 'live' || (live !== null && releaseRef === String(live.seq)));
    let viewing: Release | null;
    if (releaseRef === 'draft') viewing = draft ?? live;
    else if (releaseRef === 'live') viewing = live;
    else viewing = releases.find((r) => String(r.seq) === releaseRef) ?? null;
    const isPast = !isHead && !isLiveWhileDraft;
    const othersChanges =
      draft?.draftVersion != null && knownVersion !== null && knownSeq === draft.seq
        ? Math.max(0, draft.draftVersion - knownVersion)
        : 0;
    return {
      releaseRef,
      setReleaseRef,
      draft,
      live,
      releases,
      viewing,
      isEditable: isHead && (permissions?.canWrite ?? true),
      isPast,
      isLiveWhileDraft,
      canPublish: permissions?.canPublish ?? true,
      othersChanges,
      acknowledgeOthers,
    };
  }, [
    releaseRef,
    setReleaseRef,
    draft,
    live,
    releases,
    permissions,
    knownVersion,
    knownSeq,
    acknowledgeOthers,
  ]);
}
